/* =========================================================
   U3Faktory — Wishlist (Global JS, NO Liquid in this file)
   Exposes helpers used by the My Favorites section.
   ========================================================= */

/* ----------------------
   Utilities / Migration
   ---------------------- */

// Derive handle from a saved URL (for legacy entries)
function deriveHandleFromUrl(url) {
  try {
    const u = new URL(url, window.location.origin);
    const parts = u.pathname.split('/').filter(Boolean); // e.g., ["it","products","my-art"]
    const idx = parts.lastIndexOf('products');
    return idx >= 0 && parts[idx + 1] ? parts[idx + 1] : null;
  } catch {
    return null;
  }
}

// One-time migration: ensure entries have a handle; normalize types; keep fallback image
function migrateWishlistEntries() {
  const wishlist = JSON.parse(localStorage.getItem('wishlist')) || [];
  let changed = false;

  wishlist.forEach((item) => {
    // Ensure handle from old productUrl
    if (!item.handle && item.productUrl) {
      const h = deriveHandleFromUrl(item.productUrl);
      if (h) {
        item.handle = h;
        changed = true;
      }
    }
    // Normalize available to boolean
    if (typeof item.available === 'string') {
      item.available = item.available === 'true';
      changed = true;
    }
    // Keep a fallback image slot
    if (item.productImage && !item.fallbackImage) {
      item.fallbackImage = item.productImage;
      changed = true;
    }
    // Normalize noVariant & variantId
    if (!item.variantId || item.variantId === 'null' || item.variantId === 'undefined') {
      if (!item.noVariant) {
        item.noVariant = true;
        changed = true;
      }
      item.variantId = ''; // canonical
    } else {
      item.variantId = String(item.variantId);
    }
  });

  if (changed) localStorage.setItem('wishlist', JSON.stringify(wishlist));
}

/* --------------------------
   Fetch product in locale
   -------------------------- */

// Fetch product JSON in CURRENT locale (Shopify.routes.root carries /xx/ prefix)
async function fetchProductByHandle(handle) {
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const locale = (window.Shopify && Shopify.locale) || '';
  // cache-buster to avoid stale localized JSON
  const url = `${root}products/${handle}.js?_l=${encodeURIComponent(locale)}&t=${Date.now()}`;
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Product fetch failed for handle: ${handle}`);
  return res.json();
}

/* --------------------------
   Add / Remove / Match API
   -------------------------- */

// Add/remove handler for any .wishlist-button
function addToWishlist(event) {
  const bodyEl = document.querySelector('body');
  const button = event.currentTarget;

  const productId = button.getAttribute('data-id');
  const handle = button.getAttribute('data-handle'); // REQUIRED for hydration
  let variantId = button.getAttribute('data-variant-id');
  const productImage = button.getAttribute('data-image'); // fallback only
  const productUrl = button.getAttribute('data-url'); // legacy fallback
  const dataToggle = button.getAttribute('data-toggle') === 'true';
  const explicitNoVar = button.getAttribute('data-no-variant') === 'true';
  const vatText = button.getAttribute('data-vat-text') || '';
  const collectionList = button.getAttribute('data-collections') || '';
  const dataUnframed = button.getAttribute('data-unframed') === 'true';
  const availableRaw = button.getAttribute('data-available'); // "true"/"false" or null
  const available = availableRaw === 'true';

  // Determine if this is product-level (no variant)
  const noVariant = explicitNoVar || !variantId || variantId === 'null' || variantId === 'undefined';
  if (noVariant) variantId = '';
  else variantId = String(variantId);

  // Option capture (multilingual attribute names)
  let optionSize, optionMaterial;
  if (!noVariant) {
    optionSize =
      button.getAttribute('data-variant-option-size') || button.getAttribute('data-variant-option-dimensiune');
    optionMaterial =
      button.getAttribute('data-variant-option-material') ||
      button.getAttribute('data-variant-option-medium') ||
      button.getAttribute('data-variant-option-mediu');
  }

  // Current list
  let wishlist = JSON.parse(localStorage.getItem('wishlist')) || [];

  const existingProductLevel = wishlist.find((i) => i.productId === productId && (i.noVariant || !i.variantId));
  const existingExact = wishlist.find((i) => i.productId === productId && String(i.variantId) === variantId);

  // If button toggles whole product, remove any entries for this product
  if ((existingProductLevel || existingExact) && dataToggle) {
    wishlist = wishlist.filter((i) => i.productId !== productId);
    localStorage.setItem('wishlist', JSON.stringify(wishlist));

    button.classList.remove('match');
    const removedModal = document.querySelector('.removed-wishlist__modal');
    if (removedModal) {
      removedModal.classList.remove('hidden');
      setTimeout(() => removedModal.classList.add('hidden'), 1000);
    }
    return;
  }

  // Add new entry IF not present (product-level vs variant-level)
  const alreadySaved = noVariant ? !!existingProductLevel : !!existingExact;
  if (!alreadySaved) {
    // If adding a variant, remove product-level placeholder for same product
    if (!noVariant) {
      wishlist = wishlist.filter((i) => !(i.productId === productId && (i.noVariant || !i.variantId)));
    }

    const newItem = {
      productId,
      handle,
      variantId, // '' when product-level
      noVariant, // true when product-level
      vatText,
      collectionList,
      available, // snapshot; UI will re-hydrate
      // fallbacks for safety:
      fallbackImage: productImage,
      productUrl, // for legacy/migration
    };

    if (!noVariant) {
      if (optionSize) newItem.optionSize = optionSize;
      if (optionMaterial) newItem.optionMaterial = optionMaterial;
    }
    if (dataUnframed) newItem.dataUnframed = true;

    wishlist.unshift(newItem);
    localStorage.setItem('wishlist', JSON.stringify(wishlist));

    button.classList.add('match');
    if (!localStorage.getItem('dontShowWishlistModal')) {
      const modal = document.querySelector('.wishlist__modal');
      if (modal) {
        modal.classList.remove('hidden');
        bodyEl.classList.add('overflow-hidden');
      }
    }
  } else if (dataToggle) {
    // (kept for completeness; normally handled above)
    removeFromWishlist(productId, variantId);
    button.classList.remove('match');
  }
}

// Remove one entry (productId + variantId combo)
function removeFromWishlist(productId, variantId) {
  if (typeof variantId === 'undefined' || variantId === null) variantId = '';
  variantId = String(variantId);

  let wishlist = JSON.parse(localStorage.getItem('wishlist')) || [];
  wishlist = wishlist.filter((i) => !(i.productId === productId && String(i.variantId) === variantId));
  localStorage.setItem('wishlist', JSON.stringify(wishlist));

  const removedModal = document.querySelector('.removed-wishlist__modal');
  if (removedModal) {
    removedModal.classList.remove('hidden');
    setTimeout(() => removedModal.classList.add('hidden'), 1000);
  }
}

// Apply 'match' class to buttons according to saved entries
function checkWishlistButtons() {
  const wishlist = JSON.parse(localStorage.getItem('wishlist')) || [];
  const buttons = document.querySelectorAll('.wishlist-button');

  buttons.forEach((button) => {
    const productId = button.getAttribute('data-id');
    const buttonVariantIdRaw = button.getAttribute('data-variant-id');
    const dataToggle = button.getAttribute('data-toggle') === 'true';

    const buttonIsProductLevel =
      button.getAttribute('data-no-variant') === 'true' ||
      !buttonVariantIdRaw ||
      buttonVariantIdRaw === 'null' ||
      buttonVariantIdRaw === 'undefined';

    const buttonVariantId = buttonIsProductLevel ? '' : String(buttonVariantIdRaw);

    if (dataToggle || buttonIsProductLevel) {
      const match = wishlist.find((i) => i.productId === productId);
      if (match) button.classList.add('match');
      return;
    }

    // Variant-specific
    const match = wishlist.find((i) => String(i.variantId) === buttonVariantId);
    if (match) button.classList.add('match');
  });
}

/* --------------------------
   Modal helpers (unchanged)
   -------------------------- */

function hideWishlistModal() {
  const bodyEl = document.querySelector('body');
  const modal = document.querySelector('.wishlist__modal');
  if (modal) modal.classList.add('hidden');
  bodyEl.classList.remove('overflow-hidden');
}

function dontShowWishlistModal(event) {
  event.preventDefault();
  localStorage.setItem('dontShowWishlistModal', 'true');
  hideWishlistModal();
}

/* --------------------------
   Init / Re-bind on pages
   -------------------------- */

const reinitializeWishlist = () => {
  // Bind click to all wishlist buttons
  const buttons = document.querySelectorAll('.wishlist-button');
  if (buttons.length) {
    buttons.forEach((btn) => {
      // prevent duplicate listeners
      btn.removeEventListener('click', addToWishlist);
      btn.addEventListener('click', addToWishlist);
    });
    checkWishlistButtons();
  }

  // Modal close by X
  const closeBtn = document.querySelector('.wishlist__modal-close');
  if (closeBtn) {
    closeBtn.removeEventListener('click', hideWishlistModal);
    closeBtn.addEventListener('click', hideWishlistModal);
  }

  // Modal overlay click
  const modal = document.querySelector('.wishlist__modal');
  const wrapper = document.querySelector('.wishlist__modal-wrapper');
  if (modal && wrapper) {
    const overlayHandler = (e) => {
      if (!wrapper.contains(e.target)) hideWishlistModal();
    };
    modal.removeEventListener('click', overlayHandler);
    modal.addEventListener('click', overlayHandler);
  }

  // "Don't show again"
  const dontShowLink = document.querySelector('.dont-show-wishlist-modal');
  if (dontShowLink) {
    dontShowLink.removeEventListener('click', dontShowWishlistModal);
    dontShowLink.addEventListener('click', dontShowWishlistModal);
  }
};

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  migrateWishlistEntries();
  reinitializeWishlist();
});

/* -----------------------------------------
   Export helpers for the Section (with Liquid)
   The section will call renderWishlistSection()
   and rely on these helpers.
   ----------------------------------------- */
window.U3Wishlist = Object.assign(window.U3Wishlist || {}, {
  migrateWishlistEntries,
  fetchProductByHandle,
  deriveHandleFromUrl,
  removeFromWishlist,
  reinitializeWishlist,
});
